import React, { useState, useRef } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import "../styles/Drones.css";
import { exportToCSV } from "../utils/exportUtils";

// GraphQL Queries and Mutations
const GET_JOBS = gql`
  query {
    jobs {
      jobId
      jobName
      companyName
      jobType
      experience
      location
      salary
      description
      requirement
      status
      sellerId
      email
      phoneNumber
      image
    }
  }
`;

const UPDATE_JOB_STATUS = gql`
  mutation updateStatus($jobId: String!, $status: String!) {
    updateStatus(jobId: $jobId, status: $status) {
      jobId
      status
    }
  }
`;

const DELETE_JOB = gql`
  mutation DeleteJob($jobId: String!) {
    deleteJob(jobId: $jobId) {
      jobId
    }
  }
`;

function HireJobsDashboard() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "sellerId", direction: "asc" });
  const [viewingJob, setViewingJob] = useState(null);
  const [viewingContent, setViewingContent] = useState({ type: null, content: null, title: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPage, setProductsPage] = useState({});
  const tableRef = useRef(null);

  // Constants for pagination
  const ITEMS_PER_PAGE = 10;
  const PRODUCTS_PER_PAGE = 10;

  // Apollo Hooks
  const { loading, error: queryError, data, refetch } = useQuery(GET_JOBS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [updateStatusMutation] = useMutation(UPDATE_JOB_STATUS);
  const [deleteJobMutation] = useMutation(DELETE_JOB);

  const jobs = data?.jobs || [];
  const error = queryError ? queryError.message : null;

  // Delete job function
  const deleteJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;

    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${jobId}-deleting`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await deleteJobMutation({ variables: { jobId } });
      refetch();
      if (viewingJob && viewingJob.jobId === jobId) {
        setViewingJob(null);
      }
    } catch (err) {
      alert("❌ Failed to delete job: " + err.message);
    } finally {
      const newUpdatingIdsCopy = new Set(updatingIds);
      newUpdatingIdsCopy.delete(`${jobId}-deleting`);
      setUpdatingIds(newUpdatingIdsCopy);
    }
  };

  // Update job status
  const updateStatus = async (jobId, newStatus) => {
    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${jobId}-${newStatus}`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await updateStatusMutation({ variables: { jobId, status: newStatus } });
      refetch();
    } catch (err) {
      alert(`❌ Failed to update status: ${err.message}`);
    } finally {
      const newUpdatingIdsCopy = new Set(updatingIds);
      newUpdatingIdsCopy.delete(`${jobId}-${newStatus}`);
      setUpdatingIds(newUpdatingIdsCopy);
    }
  };

  // View content (description, requirement)
  const viewContent = (type, content, title) => {
    setViewingContent({ type, content, title });
  };

  // Close content modal
  const closeContent = () => {
    setViewingContent({ type: null, content: null, title: null });
  };

  // Group jobs by seller ID
  const groupedBySeller = jobs.reduce((acc, job) => {
    const sellerId = job.sellerId;
    if (!acc[sellerId]) {
      acc[sellerId] = {
        sellerId: sellerId,
        sellerInfo: { email: job.email, phoneNumber: job.phoneNumber },
        jobs: []
      };
    }
    acc[sellerId].jobs.push(job);
    return acc;
  }, {});

  // Convert to array and sort
  const sellerGroups = Object.values(groupedBySeller).sort((a, b) =>
    a.sellerId.localeCompare(b.sellerId)
  );

  // Calculate seller stats
  const sellerGroupsWithStats = sellerGroups.map(seller => {
    const totalJobs = seller.jobs.length;
    const approvedJobs = seller.jobs.filter(j => j.status === 'approved').length;
    const pendingJobs = seller.jobs.filter(j => !j.status || j.status === 'pending').length;
    const rejectedJobs = seller.jobs.filter(j => j.status === 'rejected').length;
    const deactivatedJobs = seller.jobs.filter(j => j.status === 'Temporarily_Deactivated').length;

    const totalSalary = seller.jobs.reduce((sum, job) => {
      const salary = parseInt(job.salary?.replace(/[^\d]/g, '') || "0");
      return sum + salary;
    }, 0);
    const avgSalary = totalJobs > 0 ? totalSalary / totalJobs : 0;

    return {
      ...seller,
      totalJobs,
      approvedJobs,
      pendingJobs,
      rejectedJobs,
      deactivatedJobs,
      avgSalary
    };
  });

  // Filter sellers based on search
  const filteredSellers = sellerGroupsWithStats.filter(seller => {
    const matchesSearch =
      searchTerm === "" ||
      seller.sellerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.phoneNumber?.includes(searchTerm) ||
      seller.jobs.some(job =>
        job.jobName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.jobType?.toLowerCase().includes(searchTerm.toLowerCase())
      );

    if (selectedStatus === "all") return matchesSearch;

    // Filter by status
    const filteredJobs = seller.jobs.filter(job => {
      const status = job.status?.toLowerCase() || "pending";
      return status === selectedStatus;
    });

    return matchesSearch && filteredJobs.length > 0;
  });

  // Sort sellers
  const sortedSellers = [...filteredSellers].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    if (key === 'sellerId' || key === 'email') {
      aVal = aVal?.toLowerCase?.() || '';
      bVal = bVal?.toLowerCase?.() || '';
    }

    if (sortConfig.direction === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination for sellers
  const totalFilteredPages = Math.ceil(sortedSellers.length / ITEMS_PER_PAGE);
  const paginatedSellers = sortedSellers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get filtered jobs for expanded seller with pagination
  const getFilteredJobsForSeller = (sellerId) => {
    const seller = groupedBySeller[sellerId];
    if (!seller) return [];

    return seller.jobs.filter(job => {
      const status = job.status?.toLowerCase() || "pending";
      return selectedStatus === "all" || status === selectedStatus;
    });
  };

  // Get paginated jobs for a seller
  const getPaginatedJobsForSeller = (sellerId) => {
    const filteredJobs = getFilteredJobsForSeller(sellerId);
    const currentProductsPage = productsPage[sellerId] || 1;
    const startIndex = (currentProductsPage - 1) * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;

    return {
      jobs: filteredJobs.slice(startIndex, endIndex),
      currentPage: currentProductsPage,
      totalPages: Math.ceil(filteredJobs.length / PRODUCTS_PER_PAGE),
      totalJobs: filteredJobs.length
    };
  };

  // Handle products page change
  const handleProductsPageChange = (sellerId, page) => {
    setProductsPage(prev => ({
      ...prev,
      [sellerId]: page
    }));
  };

  // View job details
  const viewJobDetails = (job) => {
    setViewingJob(job);
  };

  // Close job details modal
  const closeJobDetails = () => {
    setViewingJob(null);
  };

  // Handle seller page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollLeft = 0;
    }
  };

  // Status statistics
  const statusStats = {
    all: jobs.length,
    approved: jobs.filter(j => j.status === "approved").length,
    pending: jobs.filter(j => !j.status || j.status === "pending").length,
    rejected: jobs.filter(j => j.status === "rejected").length,
    temporarily_deactivated: jobs.filter(j => j.status === "Temporarily_Deactivated").length,
  };

  // Overall stats
  const totalJobs = jobs.length;
  const uniqueSellers = sellerGroups.length;
  const deactivatedJobs = jobs.filter(j => j.status === "Temporarily_Deactivated").length;

  // Calculate average salary
  const calculateAvgSalary = () => {
    const salaries = jobs
      .map(j => parseInt(j.salary?.replace(/[^\d]/g, '') || "0"))
      .filter(n => n > 0);

    if (salaries.length === 0) return 0;
    return Math.round(salaries.reduce((a, b) => a + b, 0) / salaries.length);
  };

  const avgSalary = calculateAvgSalary();

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return <span style={{ marginLeft: '4px', fontSize: '12px' }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const truncateText = (text, length = 30) => {
    if (!text) return "—";
    if (text.length <= length) return text;
    return text.substring(0, length) + "...";
  };

  const formatPrice = (price) => {
    if (!price) return "—";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Export function
  const handleExport = () => {
    const headers = ["Job ID", "Seller ID", "Title", "Company", "Location", "Experience", "Salary", "Status", "Email"];
    const rows = jobs.map(j => [
      j.jobId,
      j.sellerId,
      j.jobName,
      j.companyName,
      j.location,
      j.experience,
      j.salary,
      j.status || "pending",
      j.email || ""
    ]);
    exportToCSV(headers, rows, `hire_jobs_export_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Render content buttons for description and requirements
  const renderContentButtons = (job) => {
    const hasDescription = job.description && job.description.trim().length > 0;
    const hasRequirements = job.requirement && job.requirement.trim().length > 0;

    return (
      <div className="content-buttons-container" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
        {hasDescription && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('description', job.description, `Description - ${job.jobName}`);
            }}
            style={{
              background: '#e0f2fe',
              color: '#0369a1',
              border: '1px solid #bae6fd',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📝 Desc
          </button>
        )}

        {hasRequirements && (
          <button
            className="content-btn"
            onClick={(e) => {
              e.stopPropagation();
              viewContent('requirements', job.requirement, `Requirements - ${job.jobName}`);
            }}
            style={{
              background: '#f0f9ff',
              color: '#0c4a6e',
              border: '1px solid #e0f2fe',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📋 Reqs
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="drones-container">
      <div className="main-wrapper">
        {/* Top Navigation */}
        <div className="top-navigation">
          <div className="nav-left">
            <h1>Job Management System</h1>
            <p>Admin Dashboard • Manage Job Postings • Real-time Updates</p>
          </div>
          <div className="nav-right">
            <button
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleExport}
              disabled={loading || jobs.length === 0}
            >
              📊 Export Report
            </button>
          </div>
        </div>

        {/* Compact Stats Dashboard */}
        <div className="compact-stats-container">
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Jobs</div>
              <div className="compact-stat-value">{totalJobs}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Avg Salary</div>
              <div className="compact-stat-value">{avgSalary > 0 ? formatPrice(avgSalary) : "—"}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Sellers</div>
              <div className="compact-stat-value">{uniqueSellers}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Pending Approval</div>
              <div className="compact-stat-value">{statusStats.pending}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Deactivated</div>
              <div className="compact-stat-value">{deactivatedJobs}</div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">Job Postings Management</h2>
          </div>

          <div className="seller-selector">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by seller ID, email, job title, company, or location..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />

            </div>

            <div className="status-tabs-container">
              {["all", "approved", "pending", "rejected", "temporarily_deactivated"].map((status) => (
                <button
                  key={status}
                  className={`status-tab ${selectedStatus === status ? "active" : ""}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setCurrentPage(1);
                    setProductsPage({});
                  }}
                >
                  {status === "temporarily_deactivated" ? "Deactivated" : status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="tab-count">{statusStats[status]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => refetch()}>✕</button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Sellers Table Container */}
          <div className="table-container">
            <div className="table-header">
              <h3 className="table-title">
                All Sellers ({sellerGroups.length})
              </h3>
              <div className="table-controls">
                <div className="table-info">
                  Page {currentPage} of {totalFilteredPages || 1} • Showing {paginatedSellers.length} sellers
                </div>
              </div>
            </div>

            <div className="table-content" ref={tableRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading job data...</p>
                </div>
              ) : sellerGroups.length === 0 ? (
                <div className="empty-state">
                  <p>No sellers found</p>
                </div>
              ) : (
                <table className="sellers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("sellerId")}>
                        Seller ID <SortIcon column="sellerId" />
                      </th>
                      <th onClick={() => handleSort("email")}>
                        Contact Info <SortIcon column="email" />
                      </th>
                      <th onClick={() => handleSort("totalJobs")}>
                        Jobs <SortIcon column="totalJobs" />
                      </th>
                      <th onClick={() => handleSort("avgSalary")}>
                        Avg Salary <SortIcon column="avgSalary" />
                      </th>
                      <th>Status Breakdown</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSellers.map((seller) => {
                      const { jobs: sellerJobs, currentPage: jobsCurrentPage, totalPages: jobsTotalPages, totalJobs } = getPaginatedJobsForSeller(seller.sellerId);

                      return (
                        <React.Fragment key={seller.sellerId}>
                          <tr
                            className={`seller-row ${expandedSeller === seller.sellerId ? 'expanded' : ''}`}
                            onClick={() => setExpandedSeller(
                              expandedSeller === seller.sellerId ? null : seller.sellerId
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="seller-id-cell">
                                <span className="expand-icon">
                                  {expandedSeller === seller.sellerId ? '▼' : '▶'}
                                </span>
                                <strong>Seller #{seller.sellerId}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-contact-cell">
                                <div className="seller-email">
                                  📧 {seller.sellerInfo?.email || "No email"}
                                </div>
                                <div className="seller-phone">
                                  📱 {seller.sellerInfo?.phoneNumber || "No phone"}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="seller-products-count">
                                <span className="count-badge">{seller.totalJobs}</span>
                                <span className="count-label">jobs</span>
                              </div>
                            </td>
                            <td>
                              <div className="seller-value">
                                <strong className="value-amount">
                                  {seller.avgSalary > 0 ? formatPrice(seller.avgSalary) : "—"}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div className="status-breakdown">
                                <span className="status-dot approved" title={`${seller.approvedJobs} approved`}>
                                  {seller.approvedJobs}
                                </span>
                                <span className="status-dot pending" title={`${seller.pendingJobs} pending`}>
                                  {seller.pendingJobs}
                                </span>
                                <span className="status-dot rejected" title={`${seller.rejectedJobs} rejected`}>
                                  {seller.rejectedJobs}
                                </span>
                                <span className="status-dot deactivated" title={`${seller.deactivatedJobs} deactivated`}>
                                  {seller.deactivatedJobs}
                                </span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="action-btn action-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSeller(
                                    expandedSeller === seller.sellerId ? null : seller.sellerId
                                  );
                                }}
                                title="View Jobs"
                              >
                                {expandedSeller === seller.sellerId ? '👁️‍🗨️' : '👁️'}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Jobs Row */}
                          {expandedSeller === seller.sellerId && (
                            <tr className="products-row">
                              <td colSpan="6">
                                <div className="products-container">
                                  <div className="products-header">
                                    <div>
                                      <h4>Jobs from Seller #{seller.sellerId}</h4>
                                      <span className="products-count">
                                        {totalJobs} jobs • Showing {sellerJobs.length} on page {jobsCurrentPage} of {jobsTotalPages}
                                      </span>
                                    </div>
                                  </div>

                                  {totalJobs === 0 ? (
                                    <div className="no-products">
                                      <p>No jobs found for the selected status filter.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="products-table-container">
                                        <table className="products-table">
                                          <thead>
                                            <tr>
                                              <th>Job ID</th>
                                              <th>Image</th>
                                              <th>Job Details</th>
                                              <th>Company</th>
                                              <th>Location</th>
                                              <th>Type</th>
                                              <th>Experience</th>
                                              <th>Salary</th>
                                              <th>Status</th>
                                              <th>Content</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sellerJobs.map((job) => (
                                              <tr key={job.jobId}>
                                                <td>
                                                  <div className="id-cell">
                                                    {job.jobId}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-image-cell">
                                                    {job.image ? (
                                                      <img
                                                        src={job.image.startsWith('http') ? job.image : `${process.env.REACT_APP_UPLOAD_URL || 'http://localhost:8000'}/uploads/${job.image}`}
                                                        alt={job.jobName}
                                                        className="drone-image-preview"
                                                        style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                                        onError={(e) => {
                                                          e.target.onerror = null;
                                                          e.target.src = "https://via.placeholder.com/50?text=Job";
                                                        }}
                                                      />
                                                    ) : (
                                                      <div className="no-image-placeholder" style={{ width: '50px', height: '50px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', fontSize: '20px' }}>
                                                        💼
                                                      </div>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-details-cell">
                                                    <div>
                                                      <div className="drone-name">{job.jobName}</div>
                                                      <div className="drone-specs">
                                                        <span className="drone-brand">{job.companyName || "—"}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{job.companyName || "—"}</div>
                                                </td>
                                                <td>
                                                  <div className="drone-uin">{job.location || "Remote"}</div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{job.jobType || "—"}</div>
                                                </td>
                                                <td>
                                                  <div className="drone-uin">{job.experience || "—"}</div>
                                                </td>
                                                <td>
                                                  <div className="price-cell">
                                                    {job.salary ? `₹${job.salary}` : "—"}
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className={`status-badge status-${job.status === 'Temporarily_Deactivated' ? 'deactivated' : (job.status || "pending")}`}>
                                                    {job.status === 'Temporarily_Deactivated' ? 'DEACTIVATED' : (job.status || "pending").toUpperCase()}
                                                  </span>
                                                </td>
                                                <td style={{ maxWidth: '200px', minWidth: '150px' }}>
                                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                                      Click to view:
                                                    </div>
                                                    {renderContentButtons(job)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="action-buttons">
                                                    {/* For Pending Status */}
                                                    {(!job.status || job.status === "pending") && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateStatus(job.jobId, "approved")}
                                                          disabled={updatingIds.has(`${job.jobId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateStatus(job.jobId, "rejected")}
                                                          disabled={updatingIds.has(`${job.jobId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateStatus(job.jobId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${job.jobId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {/* For Approved Status */}
                                                    {job.status === "approved" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateStatus(job.jobId, "pending")}
                                                          disabled={updatingIds.has(`${job.jobId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateStatus(job.jobId, "rejected")}
                                                          disabled={updatingIds.has(`${job.jobId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateStatus(job.jobId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${job.jobId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {/* For Rejected Status */}
                                                    {job.status === "rejected" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateStatus(job.jobId, "pending")}
                                                          disabled={updatingIds.has(`${job.jobId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateStatus(job.jobId, "approved")}
                                                          disabled={updatingIds.has(`${job.jobId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-deactivate"
                                                          onClick={() => updateStatus(job.jobId, "Temporarily_Deactivated")}
                                                          disabled={updatingIds.has(`${job.jobId}-Temporarily_Deactivated`)}
                                                          title="Temporarily Deactivate"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-Temporarily_Deactivated`) ? "..." : "⛔"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {/* For Deactivated Status - Only Approve, Pending, Reject options */}
                                                    {job.status === "Temporarily_Deactivated" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateStatus(job.jobId, "pending")}
                                                          disabled={updatingIds.has(`${job.jobId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateStatus(job.jobId, "approved")}
                                                          disabled={updatingIds.has(`${job.jobId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateStatus(job.jobId, "rejected")}
                                                          disabled={updatingIds.has(`${job.jobId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${job.jobId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    <button
                                                      className="action-btn action-view"
                                                      onClick={() => viewJobDetails(job)}
                                                      title="View Details"
                                                    >
                                                      👁
                                                    </button>

                                                    <button
                                                      className="action-btn action-delete"
                                                      onClick={() => deleteJob(job.jobId)}
                                                      title="Delete"
                                                    >
                                                      🗑
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Jobs Pagination */}
                                      {jobsTotalPages > 1 && (
                                        <div className="products-pagination">
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, jobsCurrentPage - 1)}
                                            disabled={jobsCurrentPage === 1}
                                            className="pagination-btn"
                                          >
                                            &lt; Previous
                                          </button>
                                          <div className="pagination-info">
                                            Page {jobsCurrentPage} of {jobsTotalPages}
                                          </div>
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, jobsCurrentPage + 1)}
                                            disabled={jobsCurrentPage === jobsTotalPages}
                                            className="pagination-btn"
                                          >
                                            Next &gt;
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls for Sellers */}
            {totalFilteredPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={currentPage === num ? "active" : ""}
                    onClick={() => handlePageChange(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Job Details Modal */}
      {viewingJob && (
        <div className="modal-overlay" onClick={closeJobDetails}>
          <div className="modal-content drone-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Job Details</h2>
              <button className="close-btn" onClick={closeJobDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="drone-details-grid">
                <div className="drone-info-section">
                  <div className="info-row">
                    <div className="info-label">Job ID</div>
                    <div className="info-value">{viewingJob.jobId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Job Title</div>
                    <div className="info-value">{viewingJob.jobName}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Company</div>
                    <div className="info-value">{viewingJob.companyName || "—"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Job Type</div>
                    <div className="info-value">{viewingJob.jobType || "—"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Experience</div>
                    <div className="info-value">{viewingJob.experience || "—"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Location</div>
                    <div className="info-value">{viewingJob.location || "Remote"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Salary</div>
                    <div className="info-value price-value">
                      {viewingJob.salary ? `₹${viewingJob.salary}` : "—"}
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Status</div>
                    <div className="info-value">
                      <span className={`status-badge status-${viewingJob.status === 'Temporarily_Deactivated' ? 'deactivated' : (viewingJob.status || "pending")}`}>
                        {viewingJob.status === 'Temporarily_Deactivated' ? 'DEACTIVATED' : (viewingJob.status || "pending").toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller ID</div>
                    <div className="info-value">{viewingJob.sellerId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Email</div>
                    <div className="info-value">{viewingJob.email || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Phone</div>
                    <div className="info-value">{viewingJob.phoneNumber || "N/A"}</div>
                  </div>

                  {/* Content buttons in modal */}
                  <div className="info-row full-width">
                    <div className="info-label">Content</div>
                    <div className="info-value" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {viewingJob.description && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('description', viewingJob.description, `Description - ${viewingJob.jobName}`)}
                          style={{
                            background: '#e0f2fe',
                            color: '#0369a1',
                            border: '1px solid #bae6fd',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📝 View Description
                        </button>
                      )}

                      {viewingJob.requirement && (
                        <button
                          className="content-btn"
                          onClick={() => viewContent('requirements', viewingJob.requirement, `Requirements - ${viewingJob.jobName}`)}
                          style={{
                            background: '#f0f9ff',
                            color: '#0c4a6e',
                            border: '1px solid #e0f2fe',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          📋 View Requirements
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions in Details Modal */}
                <div className="drone-actions-section">
                  <div className="info-label">Quick Actions</div>
                  <div className="action-buttons-grid">
                    {(!viewingJob.status || viewingJob.status === "pending") && (
                      <>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "approved");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "rejected");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "Temporarily_Deactivated");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingJob.status === "approved" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "pending");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "rejected");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "Temporarily_Deactivated");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingJob.status === "rejected" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "pending");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "approved");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-deactivate"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "Temporarily_Deactivated");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-Temporarily_Deactivated`)}
                          title="Temporarily Deactivate"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-Temporarily_Deactivated`) ? "..." : "⛔ Deactivate"}
                        </button>
                      </>
                    )}

                    {viewingJob.status === "Temporarily_Deactivated" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "pending");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "approved");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateStatus(viewingJob.jobId, "rejected");
                            closeJobDetails();
                          }}
                          disabled={updatingIds.has(`${viewingJob.jobId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingJob.jobId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    <button
                      className="action-btn-large action-delete"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this job?")) {
                          deleteJob(viewingJob.jobId);
                          closeJobDetails();
                        }
                      }}
                      title="Delete"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content View Modal */}
      {viewingContent.type && (
        <div className="modal-overlay" onClick={closeContent}>
          <div className="modal-content content-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh' }}>
            <div className="modal-header">
              <h2>{viewingContent.title}</h2>
              <button className="close-btn" onClick={closeContent}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              {viewingContent.type === 'description' && (
                <div className="description-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Job Description</h3>
                  <div className="content-text" style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                    fontSize: '14px',
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    maxHeight: '60vh',
                    overflowY: 'auto'
                  }}>
                    {viewingContent.content || 'No description provided.'}
                  </div>
                </div>
              )}

              {viewingContent.type === 'requirements' && (
                <div className="requirements-content">
                  <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Job Requirements</h3>
                  <div className="content-text" style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                    fontSize: '14px',
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    maxHeight: '60vh',
                    overflowY: 'auto'
                  }}>
                    {viewingContent.content || 'No requirements specified.'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HireJobsDashboard;